import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SegmentConfig } from '../models/segments';
import { AppContext, AppProviderDispatch } from '../shared/app-context';
import useRestService from '../shared/rest-service';
import { unmapTransactionalAttributes } from '../utilities/attribute-filter';

/**
 * Hook to manage PUT requests to update a segment
 */
export function useSegmentUpdate(segmentId: string) {
  const { fetchWrapper } = useRestService();
  const queryClient = useQueryClient();
  const appState = React.useContext<AppProviderDispatch>(AppContext);

  const cleanUpNewSegment = (key, value) => {
    if (key === 'composition' && Object.keys(value).length === 0) {
      return undefined;
    }
    return value;
  };

  return useMutation<{ message: string; success: boolean }, Error, SegmentConfig>(
    (newSegment) => {
      return fetchWrapper(`api/${appState.state.clientId}/segments/${segmentId}`, {
        method: 'PUT',
        body: JSON.stringify(
          {
            ...newSegment,
            attributes: {
              ...newSegment.attributes,
              include: {
                ...newSegment.attributes.include,
                predicates: unmapTransactionalAttributes(newSegment.attributes.include.predicates)
              },
              exclude: {
                ...newSegment.attributes.exclude,
                predicates: unmapTransactionalAttributes(newSegment.attributes.exclude?.predicates)
              }
            },
            composition: {
              ...newSegment.composition
            }
          },
          cleanUpNewSegment
        )
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['segment', segmentId]);
        queryClient.invalidateQueries(['segments']);
      }
    }
  );
}

export function useSegmentSharingUpdate() {
  const { fetchWrapper } = useRestService();
  const queryClient = useQueryClient();
  const appState = React.useContext<AppProviderDispatch>(AppContext);

  return useMutation<{ message: string; success: boolean }, Error, { segmentId: number; isShared: boolean }>(
    (variables) => {
      return fetchWrapper(`api/${appState.state.clientId}/segments/${variables.segmentId}/shared`, {
        method: 'PUT',
        body: JSON.stringify({ isShared: variables.isShared })
      });
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['segment', String(variables.segmentId)]);
        queryClient.invalidateQueries(['segments']);
        queryClient.invalidateQueries(['available-compositions']);
        queryClient.invalidateQueries(['dependencies']);
      }
    }
  );
}
